import { useJob, useMe, useSession } from "@/api/hooks";
import "@fontsource/roboto/latin-ext.css";
import "@fontsource/roboto/latin.css";
import {
  ApiProvider,
  AuthProvider,
  UserInfoProvider,
  useUserInfo,
} from "@mb-pro-ui/utils";
import {
  Box,
  CircularProgress,
  createTheme,
  ThemeProvider,
} from "@mui/material";
import CssBaseline from "@mui/material/CssBaseline";
import { SnackbarProvider } from "notistack";
import { type PropsWithChildren, type ReactNode } from "react";
import {
  Redirect,
  Route,
  Router,
  Switch,
  useLocation,
  useParams,
  useSearchParams,
  type BaseLocationHook,
  type HookNavigationOptions,
  type Path,
} from "wouter";
import {
  useHistoryState,
  type BrowserLocationHook,
} from "wouter/use-browser-location";
import type { JobWithDetails } from "./api/hooks/useJob";
import type { User } from "./api/hooks/useMe";
import { usePushNotifications } from "./api/hooks/usePushNotification";
import CancelledJobDrawer from "./components/CancelledJobDrawer";
import { InterventionDetailsModal } from "./components/InterventionDetailsModal";
import { JobDetails } from "./components/JobDetails";
import { AudioProvider } from "./contexts/AudioContexts";
import { JobsProvider, useJobsContext } from "./contexts/JobsContexts";
import { PushProvider } from "./contexts/PushContexts";
import { WakeLocked } from "./contexts/WakeLocked";
import LocaleProvider from "./locales/LocaleProvider";
import Login from "./Login";
import { JobList } from "./pages/JobList";
import { ShiftSelector } from "./pages/ShiftSelector";

declare module "@mui/material/styles" {
  interface TypographyVariants {
    "raptor-main-h": React.CSSProperties;
    "raptor-sub-h": React.CSSProperties;
    "raptor-main-p": React.CSSProperties;
    "raptor-sub-p": React.CSSProperties;
    "raptor-button-text": React.CSSProperties;
  }

  interface TypographyVariantsOptions {
    "raptor-main-h"?: React.CSSProperties;
    "raptor-sub-h"?: React.CSSProperties;
    "raptor-main-p"?: React.CSSProperties;
    "raptor-sub-p"?: React.CSSProperties;
    "raptor-button-text"?: React.CSSProperties;
  }
}

declare module "@mui/material/Typography" {
  interface TypographyPropsVariantOverrides {
    "raptor-main-h": true;
    "raptor-sub-h": true;
    "raptor-main-p": true;
    "raptor-sub-p": true;
    "raptor-button-text": true;
  }
}

let theme = createTheme({
  palette: {
    primary: {
      main: "#2587BC",
      contrastText: "#FFFFFF",
    },
  },
  typography: {
    "raptor-main-h": {
      fontSize: "2rem",
      fontWeight: 700,
      lineHeight: 1.2,
    },
  },
  components: {
    MuiInputBase: {
      styleOverrides: {
        // root: {
        //   "& input::placeholder": {
        //     fontSize: "0.8rem",
        //   },
        // },
        input: {
          fontSize: "1rem",
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontSize: "1rem",
        },
      },
    },
    MuiBadge: {
      styleOverrides: {
        badge: {
          color: "#FFFFFF",
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          textTransform: "none",
          fontWeight: 800,
          minHeight: 48,
        },
      },
    },
    MuiTypography: {
      defaultProps: {
        variantMapping: {
          "raptor-main-h": "h1",
          "raptor-sub-h": "h2",
          "raptor-main-p": "p",
          "raptor-sub-p": "p",
          "raptor-button-text": "span",
        },
      },
    },
  },
});

// breakpoints csak a kész theme után használható
theme = createTheme(theme, {
  typography: {
    "raptor-main-h": {
      color: "#1a1a1a",
      fontSize: "1rem",
      [theme.breakpoints.up("sm")]: {
        fontSize: "1.1rem",
      },
      [theme.breakpoints.up("md")]: {
        fontSize: "1.2rem",
      },
    },
    "raptor-sub-h": {
      color: "#1a1a1a",
      fontSize: "0.9rem",
      [theme.breakpoints.up("sm")]: {
        fontSize: "1rem",
      },
      [theme.breakpoints.up("md")]: {
        fontSize: "1.1rem",
      },
    },
    "raptor-main-p": {
      color: "#424242",
      fontWeight: 500,
      fontSize: "0.9rem",
      [theme.breakpoints.up("sm")]: {
        fontSize: "0.95rem",
      },
      [theme.breakpoints.up("md")]: {
        fontSize: "1rem",
      },
    },
    "raptor-sub-p": {
      color: "#424242",
      fontWeight: 500,
      fontSize: "0.85rem",
      [theme.breakpoints.up("sm")]: {
        fontSize: "0.9rem",
      },
      [theme.breakpoints.up("md")]: {
        fontSize: "0.95rem",
      },
    },
    "raptor-button-text": {
      color: "#FFFFFF",
      fontWeight: 500,
      fontSize: "0.9rem",
      [theme.breakpoints.up("sm")]: {
        fontSize: "0.95rem",
      },
      [theme.breakpoints.up("md")]: {
        fontSize: "1rem",
      },
    },
  },
});

const BASE_URLS = {
  LOCAL: "http://localhost:7000",
  REMOTE: "/api",
  SA: "https://sa.monitoringbook.com/api",
  Dev: "https://dev.monitoringbook.com/api",
};

function CommonProviders({ children }: PropsWithChildren) {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline enableColorScheme />
      <SnackbarProvider
        hideIconVariant
        autoHideDuration={5000}
        anchorOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        style={{
          background: "#2587bc",
          fontPalette: "#FFFFFF",
        }}
      >
        <UserInfoProvider>
          <ApiProvider baseUrl={BASE_URLS.REMOTE} websocket={false} cacheBust="2602191601">
            <AuthProvider>
              <LocaleProvider>{children}</LocaleProvider>
            </AuthProvider>
          </ApiProvider>
        </UserInfoProvider>
      </SnackbarProvider>
    </ThemeProvider>
  );
}

export const RedirectFrom = <H extends BaseLocationHook = BrowserLocationHook>({
  state,
  ...props
}: HookNavigationOptions<H> & { to: Path; href?: never }) => {
  const [location] = useLocation();
  return <Redirect {...props} state={{ ...state, from: location }} replace />;
};

type PrivateRootChildren =
  | ReactNode
  | ((props: { me: ReturnType<typeof useMe>["data"] }) => ReactNode);

function PrivateRootComponent({ children }: { children: PrivateRootChildren }) {
  const [sid] = useUserInfo();
  const me = useMe();

  usePushNotifications();

  if (!sid) {
    return <RedirectFrom to="/login" />;
  }

  if (me.isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>{typeof children === "function" ? children({ me: me.data }) : children}</>
  );
}

function SessionRootComponent({ children }: PropsWithChildren) {
  const session = useSession();

  return session.isLoading ? (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
      }}
    >
      <CircularProgress />
    </Box>
  ) : session.data && !session.isError ? (
    <>{children}</>
  ) : (
    <RedirectFrom to="/start-session" />
  );
}

function StartSession() {
  const session = useSession();

  const state = useHistoryState();
  const [search] = useSearchParams();

  if (!session.isLoading && !session.isError && session.data) {
    const from =
      typeof state === "object" && !!state && typeof state.from === "string"
        ? state.from
        : search.get("from") || "/jobs";

    return <Redirect to={from} replace />;
  }

  return session.isLoading ? (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
      }}
    >
      <CircularProgress />
    </Box>
  ) : (
    <ShiftSelector />
  );
}

function Jobs({ me }: { me: User | undefined }) {
  const { jobs, isLoading, refetch } = useJobsContext();
  const session = useSession();

  return !jobs ? (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
      }}
    >
      <CircularProgress />
    </Box>
  ) : (
    <JobList
      jobs={jobs ?? []}
      isLoading={isLoading || !session}
      refetch={refetch}
      me={me}
    />
  );
}

function JobRootComponent({
  children,
}: {
  children: (props: { job: JobWithDetails; refetch: () => void }) => ReactNode;
}) {
  const { jobId } = useParams<{ jobId: string }>();
  const { data: job, refetch, isLoading, isError } = useJob(jobId);

  return isLoading ? (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
      }}
    >
      <CircularProgress />
    </Box>
  ) : job && !isError ? (
    <>{children({ job: job, refetch })}</>
  ) : (
    <RedirectFrom to="/jobs" />
  );
}

export default function App() {
  const [_, navigate] = useLocation();
  return (
    <div style={{ background: "#fcfcfc" }}>
      <CommonProviders>
        <PushProvider>
          <Router>
            <Switch>
              <Route path="/login">
                <Login />
              </Route>

              <Route path="/start-session">
                <PrivateRootComponent>
                  <StartSession />
                </PrivateRootComponent>
              </Route>

              <PrivateRootComponent>
                {({ me }) => (
                  <SessionRootComponent>
                    <WakeLocked />
                    <JobsProvider>
                      <AudioProvider>
                        <Route path="/jobs">
                          <Jobs me={me} />
                        </Route>

                        <Route path="/jobs/:jobId">
                          <JobRootComponent>
                            {({ job, refetch: refetchJob }) => (
                              <JobDetails
                                job={job}
                                refetch={refetchJob}
                                me={me}
                                onBack={() => navigate("/jobs")}
                              />
                            )}
                          </JobRootComponent>
                        </Route>

                        <Route path="/jobs/:jobId/intervention-details">
                          <JobRootComponent>
                            {({ job }) => (
                              <InterventionDetailsModal open job={job} />
                            )}
                          </JobRootComponent>
                        </Route>

                        <CancelledJobDrawer />

                        <Route path="/">
                          <RedirectFrom to="/jobs" />
                        </Route>
                      </AudioProvider>
                    </JobsProvider>
                  </SessionRootComponent>
                )}
              </PrivateRootComponent>
            </Switch>
          </Router>
        </PushProvider>
      </CommonProviders>
    </div>
  );
}
